import { create } from 'zustand';
import { Invoice } from '@/types/invoice';
import { invoiceService } from '@/services/invoiceService';

interface InvoiceStore {
  invoices: Invoice[];
  isLoading: boolean;
  error: string | null;
  
  fetchInvoices: () => Promise<void>;
  createInvoice: (invoice: Partial<Invoice>) => Promise<Invoice>;
  updateInvoice: (id: string, updates: Partial<Invoice>) => Promise<void>;
  archiveInvoice: (id: string, reason?: string) => Promise<void>;
}

export const useInvoiceStore = create<InvoiceStore>((set, get) => ({
  invoices: [],
  isLoading: false,
  error: null,

  fetchInvoices: async () => {
    set({ isLoading: true, error: null });
    try {
      const invoices = await invoiceService.fetchInvoices() as Invoice[];
      set({ invoices, isLoading: false });
    } catch (err: unknown) {
      set({ error: err.message, isLoading: false });
    }
  },

  createInvoice: async (invoice) => {
    try {
      const newInvoice = await invoiceService.createInvoice(invoice);
      set({ invoices: [newInvoice, ...get().invoices] });
      return newInvoice;
    } catch (err: unknown) {
      set({ error: err.message });
      throw err;
    }
  },

  updateInvoice: async (id, updates) => {
    try {
      const updated = await invoiceService.updateInvoice(id, updates);
      set({
        invoices: get().invoices.map(inv => inv.id === id ? updated : inv)
      });
    } catch (err: unknown) {
      set({ error: err.message });
      throw err;
    }
  },

  archiveInvoice: async (id, reason) => {
    try {
      await invoiceService.archiveInvoice(id, reason);
      // Remove it from the local list since it's archived
      set({
        invoices: get().invoices.filter(inv => inv.id !== id)
      });
    } catch (err: unknown) {
      set({ error: err.message });
      throw err;
    }
  }
}));
